import { jsxs, jsx } from "react/jsx-runtime";
import { Alert } from "@zillow/constellation";
import { event } from "@zillow/universal-analytics";
import { mapValues } from "lodash";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { useForm, FormProvider, useFormState } from "react-hook-form";
import { getCmsInfoForPage } from "../../utils/clickstream/getCmsInfoForPage.esm.js";
import { getTrackingParamsFromCurrentUrl } from "../../utils/utm.esm.js";
import { usePageProps } from "../PagePropsProvider/PagePropsProvider.esm.js";
const ApexForm = ({
  businessLine,
  children,
  defaultValues,
  endpoint,
  id,
  warmLeadSource
}) => {
  const pageProps = usePageProps();
  const initialDefaultValues = {
    ...defaultValues,
    Business_Line: businessLine,
    Warm_Lead_Source: warmLeadSource,
    utm_medium: "",
    utm_term: "",
    utm_content: "",
    utm_source: "",
    utm_campaign: "",
    Internal_Tracking_Code: "",
    Test_Variation: "",
    URL: "",
    URL_Referrer: ""
  };
  const form = useForm({
    mode: "onTouched",
    defaultValues: initialDefaultValues
  });
  useEffect(
    () => {
      const currentTrackingValues = getTrackingParamsFromCurrentUrl();
      form.reset({
        ...initialDefaultValues,
        ...currentTrackingValues
      }, {
        keepErrors: true,
        keepDirty: true,
        keepDirtyValues: true,
        keepDefaultValues: false,
        keepValues: false,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true
      });
    },
    // Intentionally leaving out form.reset as this is a mount only effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const submit = (e) => {
    const newLaneData = {
      // https://dataportal.zillowgroup.net/event-registry/2055
      envelope: {
        event_type_id: "2055",
        event_type_version_id: "1",
        event_template_id: "126",
        event_template_version_id: "1"
        // Master ID: 2055.1.126.1
      },
      clickstream_trigger: {
        trigger_type_nm: "interaction",
        trigger_location_nm: "cms",
        trigger_source_nm: "cms_button",
        trigger_object_nm: "no_trigger_object"
      },
      semantic: {
        semantic_event_nm: "cms_submit"
      },
      cms_info: {
        ...getCmsInfoForPage(pageProps),
        block_id: id || "",
        block_type_cd: "apex_form",
        form_business_line_txt: e.target.Business_Line.value,
        form_warm_lead_source_txt: e.target.Warm_Lead_Source.value,
        itc: e.target.Internal_Tracking_Code.value
        // These fields are added in based on success or failure or validation error.
        // // form_is_valid_ind: true | false,
        // // form_invalid_fields_txt: {},
        // // form_submit_success_ind: true | false,
        // // form_server_response_message_txt: '',
        // This field is crossed out on https://docs.google.com/spreadsheets/d/13sPb86043ojCqishXpIT30LLLFnK8C_pSyGoMbfNG9E/
        // so it's probably needed.
        // channel: ''
      }
    };
    form.handleSubmit(async (initialValues, e2) => {
      const finalValues = {
        ...initialValues
      };
      if (finalValues.Name) {
        const [firstName, ...lastNameParts] = finalValues.Name.split(" ");
        const lastName = lastNameParts.join(" ");
        if (!finalValues.First_Name) {
          finalValues.First_Name = firstName;
        }
        if (!finalValues.Last_Name) {
          finalValues.Last_Name = lastName;
        }
      }
      const form2 = e2.target;
      const hiddenInputs = form2.querySelectorAll("[type=hidden]");
      hiddenInputs.forEach((input) => {
        finalValues[input.name] = input.value;
      });
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: new URLSearchParams({
          ...finalValues,
          tfa_dbFormId: "WORDPRESS",
          Ok_to_contact: 1
        }).toString()
      });
      if (res.status !== 200) {
        throw new Error(`Got failed status code: "${res.status}"`);
      }
      event({
        ...newLaneData,
        cms_info: {
          ...newLaneData.cms_info,
          form_is_valid_ind: true,
          form_invalid_fields_txt: [],
          form_submit_success_ind: res.status === 200,
          form_server_response_message_txt: await res.text()
        }
      });
    }, (errors) => {
      event({
        ...newLaneData,
        cms_info: {
          ...newLaneData.cms_info,
          form_is_valid_ind: false,
          // Keys are field names
          form_invalid_fields_txt: mapValues(errors, ({
            message,
            ref
          }) => ({
            error: message,
            value: ref.value
          })),
          form_submit_success_ind: false,
          form_server_response_message_txt: ""
        }
      });
    })(e).catch(() => {
    });
  };
  return /* @__PURE__ */ jsxs(FormProvider, { ...form, children: [
    /* @__PURE__ */ jsxs("form", { role: "form", onSubmit: submit, id, noValidate: true, children: [
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("Business_Line") }),
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("Warm_Lead_Source") }),
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("utm_medium") }),
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("utm_term") }),
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("utm_content") }),
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("utm_source") }),
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("utm_campaign") }),
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("Internal_Tracking_Code") }),
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("URL") }),
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("URL_Referrer") }),
      /* @__PURE__ */ jsx("input", { type: "hidden", ...form.register("Test_Variation") }),
      children
    ] }),
    /* @__PURE__ */ jsx(SubmissionError, {})
  ] });
};
function SubmissionError() {
  const formState = useFormState();
  const didSubmissionFail = formState.isSubmitted && !formState.isSubmitting && !formState.isSubmitSuccessful;
  return didSubmissionFail && /* @__PURE__ */ jsx(Alert, { marginTop: "sm", appearance: "error", body: "An error occurred while processing your request. Review the information to ensure it's correct, then try again." });
}
ApexForm.propTypes = {
  defaultValues: PropTypes.shape({}),
  children: PropTypes.node.isRequired,
  businessLine: PropTypes.string.isRequired,
  warmLeadSource: PropTypes.string.isRequired,
  id: PropTypes.string
};
ApexForm.defaultProps = {
  defaultValues: void 0,
  id: void 0
};
export {
  ApexForm as default
};
